import { predictWin, rate, rating } from "openskill";

import type { Player } from "@/types";
import type { PlayerId } from "@/types/branded";
import { toPlayerId } from "@/types/branded";
import { calculatePoints, type MultiplierFn } from "../config/multipliers";

// ── Configuration ──────────────────────────────────────────────

export type ScoringConfig = {
  scale: number;
  clampMin: number;
  clampMax: number;
  upsetEnabled: boolean;
  upsetThreshold: number;
  upsetMaxBonus: number;
  streakEnabled: boolean;
  streakMultiplier: number;
  streakMaxBonus: number;
  fatigueEnabled: boolean;
  fatigueHalfLife: number;
  momentumEnabled: boolean;
  momentumFactor: number;
  momentumCap: number;
  comebackEnabled: boolean;
  comebackThreshold: number;
  comebackMaxBonus: number;
  consistencyEnabled: boolean;
  consistencySigmaThreshold: number;
  consistencyBonus: number;
  closenessEnabled: boolean;
  closenessMaxDelta: number;
  closenessBonus: number;
  activityWeight: number;
  activityBeta: number;
  varietyEnabled: boolean;
  varietyDecay: number;
  varietyBonus: number;
  rivalryEnabled: boolean;
  rivalryLookback: number;
  rivalryBonus: number;
};

export const defaultScoringConfig: ScoringConfig = {
  scale: 50,
  clampMin: -100,
  clampMax: 100,
  upsetEnabled: true,
  upsetThreshold: 0.4,
  upsetMaxBonus: 0.4,
  streakEnabled: true,
  streakMultiplier: 0.08,
  streakMaxBonus: 0.4,
  fatigueEnabled: false,
  fatigueHalfLife: 20,
  momentumEnabled: false,
  momentumFactor: 0.05,
  momentumCap: 0.3,
  comebackEnabled: false,
  comebackThreshold: 20,
  comebackMaxBonus: 0.5,
  consistencyEnabled: false,
  consistencySigmaThreshold: 6,
  consistencyBonus: 0.15,
  closenessEnabled: false,
  closenessMaxDelta: 0.5,
  closenessBonus: 0.2,
  activityWeight: 1,
  activityBeta: 5,
  varietyEnabled: true,
  varietyDecay: 3,
  varietyBonus: 0.2,
  rivalryEnabled: true,
  rivalryLookback: 10,
  rivalryBonus: 0.25,
};

export type SimulationConfig = {
  numPlayers: number;
  numMatches: number;
  teamSize: number;
  numTeams: number;
  trueSkillSpread: number;
  scoring: ScoringConfig;
};

export const defaultSimConfig: SimulationConfig = {
  numPlayers: 8,
  numMatches: 100,
  teamSize: 1,
  numTeams: 2,
  trueSkillSpread: 8,
  scoring: { ...defaultScoringConfig },
};

// ── Simulation types ───────────────────────────────────────────

type SimPlayer = Player & { trueSkill: number };

export type MatchSnapshot = {
  matchIndex: number;
  playerSnapshots: {
    playerId: PlayerId;
    name: string;
    trueSkill: number;
    estimatedSkill: number;
    mu: number;
    sigma: number;
    totalPoints: number;
    matchPoints: number;
    rank: number;
  }[];
};

export type SimulationResult = {
  config: SimulationConfig;
  players: {
    id: PlayerId;
    name: string;
    trueSkill: number;
    finalMu: number;
    finalSigma: number;
    finalEstimatedSkill: number;
    finalTotalPoints: number;
    gamesPlayed: number;
    winRate: number;
    convergenceMatch: number;
  }[];
  matchSnapshots: MatchSnapshot[];
  analytics: SimulationAnalytics;
};

export type SimulationAnalytics = {
  skillCorrelation: number;
  pointsCorrelation: number;
  skillMAE: number;
  rankMAE: number;
  convergenceMatch: number;
  pointDistribution: { bucketMin: number; bucketMax: number; count: number }[];
  multiplierImpact: Record<string, number>;
  predictionAccuracy: number;
  sigmaOverTime: number[];
  /** Gini coefficient of total points (0=equal, 1=unequal) */
  giniCoefficient: number;
  /** Head-to-head win counts */
  headToHead: { playerIds: PlayerId[]; names: string[]; wins: number[][] };
  /** Win rate by skill tier */
  winRateByTier: {
    tier: string;
    avgTrueSkill: number;
    winRate: number;
    avgPoints: number;
  }[];
  /** Per-player convergence */
  perPlayerConvergence: {
    playerId: PlayerId;
    name: string;
    trueSkill: number;
    convergenceMatch: number;
  }[];
  /** 95% confidence intervals */
  confidenceIntervals: {
    playerId: PlayerId;
    name: string;
    trueSkill: number;
    lower: number;
    upper: number;
    estimatedSkill: number;
    withinCI: boolean;
  }[];
  /** ELO baseline correlation */
  eloBaselineCorrelation: number;
  /** Points volatility (stddev) */
  pointsVolatility: number;
  /** Ranking stability: overlap ratio of top-K between consecutive matches */
  rankingStability: number[];
};

// ── Parameter sweep ────────────────────────────────────────────

export type SweepParam = keyof ScoringConfig;
export type SweepResult = {
  param: SweepParam;
  values: number[];
  results: {
    value: number;
    skillCorrelation: number;
    pointsCorrelation: number;
    gini: number;
    predictionAccuracy: number;
    convergenceMatch: number;
    rankMAE: number;
  }[];
};

export const runParameterSweep = (
  baseConfig: SimulationConfig,
  param: SweepParam,
  values: number[],
): SweepResult => {
  const results = values.map((value) => {
    const config: SimulationConfig = {
      ...baseConfig,
      scoring: { ...baseConfig.scoring, [param]: value },
    };
    const sim = runSimulation(config);
    return {
      value,
      skillCorrelation: sim.analytics.skillCorrelation,
      pointsCorrelation: sim.analytics.pointsCorrelation,
      gini: sim.analytics.giniCoefficient,
      predictionAccuracy: sim.analytics.predictionAccuracy,
      convergenceMatch: sim.analytics.convergenceMatch,
      rankMAE: sim.analytics.rankMAE,
    };
  });
  return { param, values, results };
};

// ── A/B Comparison ─────────────────────────────────────────────

export type ABResult = {
  configA: SimulationConfig;
  configB: SimulationConfig;
  resultA: SimulationResult;
  resultB: SimulationResult;
  comparison: {
    metric: string;
    valueA: number;
    valueB: number;
    winner: "A" | "B" | "tie";
    delta: number;
  }[];
};

export const runABComparison = (configA: SimulationConfig, configB: SimulationConfig): ABResult => {
  const resultA = runSimulation(configA);
  const resultB = runSimulation(configB);

  const metrics: {
    name: string;
    a: number;
    b: number;
    higherIsBetter: boolean;
  }[] = [
    {
      name: "Skill Correlation",
      a: resultA.analytics.skillCorrelation,
      b: resultB.analytics.skillCorrelation,
      higherIsBetter: true,
    },
    {
      name: "Points Correlation",
      a: resultA.analytics.pointsCorrelation,
      b: resultB.analytics.pointsCorrelation,
      higherIsBetter: true,
    },
    {
      name: "Prediction Accuracy",
      a: resultA.analytics.predictionAccuracy,
      b: resultB.analytics.predictionAccuracy,
      higherIsBetter: true,
    },
    {
      name: "Skill MAE",
      a: resultA.analytics.skillMAE,
      b: resultB.analytics.skillMAE,
      higherIsBetter: false,
    },
    {
      name: "Rank MAE",
      a: resultA.analytics.rankMAE,
      b: resultB.analytics.rankMAE,
      higherIsBetter: false,
    },
    {
      name: "Gini Coefficient",
      a: resultA.analytics.giniCoefficient,
      b: resultB.analytics.giniCoefficient,
      higherIsBetter: false,
    },
    {
      name: "Convergence Match",
      a: resultA.analytics.convergenceMatch,
      b: resultB.analytics.convergenceMatch,
      higherIsBetter: false,
    },
    {
      name: "Points Volatility",
      a: resultA.analytics.pointsVolatility,
      b: resultB.analytics.pointsVolatility,
      higherIsBetter: false,
    },
  ];

  const comparison = metrics.map(({ name, a, b, higherIsBetter }) => ({
    metric: name,
    valueA: a,
    valueB: b,
    winner:
      Math.abs(a - b) < 0.001
        ? ("tie" as const)
        : higherIsBetter
          ? a > b
            ? ("A" as const)
            : ("B" as const)
          : a < b
            ? ("A" as const)
            : ("B" as const),
    delta: a - b,
  }));

  return { configA, configB, resultA, resultB, comparison };
};

// ── Helpers ────────────────────────────────────────────────────

const gaussianRandom = (mean: number, stddev: number): number => {
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + z * stddev;
};

const pearsonCorrelation = (x: number[], y: number[]): number => {
  const n = x.length;
  if (n === 0) return 0;
  const meanX = x.reduce((s, v) => s + v, 0) / n;
  const meanY = y.reduce((s, v) => s + v, 0) / n;
  let num = 0,
    denX = 0,
    denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i]! - meanX;
    const dy = y[i]! - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const den = Math.sqrt(denX * denY);
  return den === 0 ? 0 : num / den;
};

const rankArray = (values: number[]): number[] => {
  const indexed = values.map((v, i) => ({ v, i }));
  indexed.sort((a, b) => b.v - a.v);
  const ranks = new Array<number>(values.length);
  indexed.forEach((item, rank) => {
    ranks[item.i] = rank + 1;
  });
  return ranks;
};

const giniCoefficient = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  if (n === 0) return 0;
  const totalSum = sorted.reduce((s, v) => s + v, 0);
  if (totalSum === 0) return 0;
  let gini = 0;
  for (let i = 0; i < n; i++) {
    gini += (2 * (i + 1) - n - 1) * sorted[i]!;
  }
  return gini / (n * totalSum);
};

const calcStddev = (values: number[]): number => {
  const n = values.length;
  if (n === 0) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / n;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / n;
  return Math.sqrt(variance);
};

const makeMultipliers = (config: ScoringConfig): Record<string, MultiplierFn> => {
  const multipliers: Record<string, MultiplierFn> = {};

  multipliers.weight = () => config.activityWeight;

  if (config.upsetEnabled) {
    multipliers.upset = ({ expectedWin }) =>
      expectedWin < config.upsetThreshold
        ? 1 + Math.min((0.5 - expectedWin) * 2, config.upsetMaxBonus)
        : 1;
  }

  if (config.streakEnabled) {
    multipliers.streak = ({ deltaMu, player }) =>
      deltaMu > 0
        ? 1 + Math.min(player.stats.winStreak * config.streakMultiplier, config.streakMaxBonus)
        : 1;
  }

  if (config.fatigueEnabled) {
    multipliers.fatigue = ({ player }) =>
      Math.pow(0.5, player.stats.gamesPlayed / config.fatigueHalfLife);
  }

  if (config.momentumEnabled) {
    multipliers.momentum = ({ deltaMu, player }) => {
      if (deltaMu <= 0) return 1;
      return 1 + Math.min(player.stats.winStreak * config.momentumFactor, config.momentumCap);
    };
  }

  if (config.comebackEnabled) {
    multipliers.comeback = ({ deltaMu, player }) => {
      if (deltaMu <= 0) return 1;
      const deficit = 25 - player.rating.mu;
      if (deficit > config.comebackThreshold) {
        return 1 + Math.min((deficit - config.comebackThreshold) * 0.02, config.comebackMaxBonus);
      }
      return 1;
    };
  }

  if (config.consistencyEnabled) {
    multipliers.consistency = ({ player }) => {
      if (player.rating.sigma < config.consistencySigmaThreshold) {
        const factor = 1 - player.rating.sigma / config.consistencySigmaThreshold;
        return 1 + factor * config.consistencyBonus;
      }
      return 1;
    };
  }

  if (config.closenessEnabled) {
    multipliers.closeness = ({ deltaMu }) => {
      const absDelta = Math.abs(deltaMu);
      if (absDelta < config.closenessMaxDelta) {
        return 1 + (1 - absDelta / config.closenessMaxDelta) * config.closenessBonus;
      }
      return 1;
    };
  }

  return multipliers;
};

const simulateOutcome = (teams: SimPlayer[][]): number[] => {
  const performances = teams.map((team) => {
    const avgSkill = team.reduce((s, p) => s + p.trueSkill, 0) / team.length;
    return avgSkill + gaussianRandom(0, 3);
  });
  const indexed = performances.map((perf, i) => ({ perf, i }));
  indexed.sort((a, b) => b.perf - a.perf);
  const ranks = new Array<number>(teams.length);
  indexed.forEach((item, rank) => {
    ranks[item.i] = rank + 1;
  });
  return ranks;
};

// ── Simple ELO baseline for comparison ─────────────────────────

const runEloBaseline = (
  simPlayers: SimPlayer[],
  numMatches: number,
  teamSize: number,
  numTeams: number,
): number => {
  const K = 32;
  const eloRatings = new Map<PlayerId, number>();
  for (const p of simPlayers) eloRatings.set(p.id, 1500);

  const playersPerMatch = teamSize * numTeams;

  for (let m = 0; m < numMatches; m++) {
    const shuffled = [...simPlayers].sort(() => Math.random() - 0.5);
    const matchPlayers = shuffled.slice(0, Math.min(playersPerMatch, simPlayers.length));

    const teams: SimPlayer[][] = [];
    for (let t = 0; t < numTeams; t++) {
      teams.push(matchPlayers.slice(t * teamSize, (t + 1) * teamSize));
    }
    if (teams.some((t) => t.length === 0)) continue;

    const ranks = simulateOutcome(teams);

    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        const avgEloI =
          teams[i]!.reduce((s, p) => s + (eloRatings.get(p.id) ?? 1500), 0) / teams[i]!.length;
        const avgEloJ =
          teams[j]!.reduce((s, p) => s + (eloRatings.get(p.id) ?? 1500), 0) / teams[j]!.length;
        const expectedI = 1 / (1 + Math.pow(10, (avgEloJ - avgEloI) / 400));
        const actualI = ranks[i]! < ranks[j]! ? 1 : ranks[i]! > ranks[j]! ? 0 : 0.5;

        for (const p of teams[i]!) {
          eloRatings.set(p.id, (eloRatings.get(p.id) ?? 1500) + K * (actualI - expectedI));
        }
        for (const p of teams[j]!) {
          eloRatings.set(
            p.id,
            (eloRatings.get(p.id) ?? 1500) + K * (1 - actualI - (1 - expectedI)),
          );
        }
      }
    }
  }

  const trueSkills = simPlayers.map((p) => p.trueSkill);
  const eloValues = simPlayers.map((p) => eloRatings.get(p.id) ?? 1500);
  return pearsonCorrelation(trueSkills, eloValues);
};

// ── Main simulation ────────────────────────────────────────────

export const runSimulation = (config: SimulationConfig): SimulationResult => {
  const { numPlayers, numMatches, teamSize, numTeams, trueSkillSpread, scoring } = config;
  const playersPerMatch = teamSize * numTeams;

  const players: SimPlayer[] = [];
  for (let i = 0; i < numPlayers; i++) {
    const trueSkill = gaussianRandom(25, trueSkillSpread);
    const id = toPlayerId(`sim-${i}`);
    players.push({
      id,
      name: `P${i + 1}`,
      rating: rating(),
      totalPoints: 0,
      stats: { gamesPlayed: 0, winStreak: 0 },
      bettingBankroll: 100,
      bettingStats: { totalBets: 0, correctBets: 0, netEarnings: 0 },
      personalBests: { highestPoints: 0, longestStreak: 0, bestRating: 0 },
      trueSkill,
    });
  }

  const multipliers = makeMultipliers(scoring);
  const matchSnapshots: MatchSnapshot[] = [];
  const allMatchPoints: number[] = [];
  const breakdownSums: Record<string, number> = {};
  let breakdownCount = 0;
  let correctPredictions = 0;
  let totalPredictions = 0;
  const sigmaOverTime: number[] = [];

  // Head-to-head tracking
  const h2hWins: number[][] = Array.from(
    { length: numPlayers },
    () => new Array(numPlayers).fill(0) as number[],
  );
  const playerWins: number[] = new Array(numPlayers).fill(0) as number[];
  const playerGames: number[] = new Array(numPlayers).fill(0) as number[];

  // Per-player convergence
  const playerConvergence: number[] = new Array(numPlayers).fill(numMatches) as number[];
  const playerConverged: boolean[] = new Array(numPlayers).fill(false) as boolean[];

  for (let m = 0; m < numMatches; m++) {
    const shuffled = [...players].sort(() => Math.random() - 0.5);
    const matchPlayers = shuffled.slice(0, Math.min(playersPerMatch, numPlayers));

    const teams: SimPlayer[][] = [];
    for (let t = 0; t < numTeams; t++) {
      teams.push(matchPlayers.slice(t * teamSize, (t + 1) * teamSize));
    }
    if (teams.some((t) => t.length === 0)) continue;

    const ranks = simulateOutcome(teams);
    const teamsRatings = teams.map((t) => t.map((p) => p.rating));
    const predictions = predictWin(teamsRatings);
    const newRatings = rate(teamsRatings, {
      rank: ranks,
      beta: scoring.activityBeta,
    });

    const bestPrediction = predictions.indexOf(Math.max(...predictions));
    const bestActual = ranks.indexOf(1);
    if (bestPrediction === bestActual) correctPredictions++;
    totalPredictions++;

    // Head-to-head
    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        const winnerTeam = ranks[i]! < ranks[j]! ? i : ranks[j]! < ranks[i]! ? j : -1;
        const loserTeam = winnerTeam === i ? j : winnerTeam === j ? i : -1;
        if (winnerTeam >= 0 && loserTeam >= 0) {
          for (const pw of teams[winnerTeam]!) {
            for (const pl of teams[loserTeam]!) {
              const iw = players.indexOf(pw);
              const il = players.indexOf(pl);
              if (iw >= 0 && il >= 0) h2hWins[iw]![il]!++;
            }
          }
        }
      }
    }

    const playerSnapshots: MatchSnapshot["playerSnapshots"] = [];

    teams.forEach((team, teamIdx) => {
      team.forEach((player, playerIdx) => {
        const oldRating = { ...player.rating };
        const newRating = newRatings[teamIdx]![playerIdx]!;
        const deltaMu = newRating.mu - oldRating.mu;
        const expectedWin = predictions[teamIdx]!;

        const { points, breakdown } = calculatePoints({
          deltaMu,
          sigma: oldRating.sigma,
          expectedWin,
          player,
          multipliers,
        });

        allMatchPoints.push(points);

        for (const [key, value] of Object.entries(breakdown)) {
          breakdownSums[key] = (breakdownSums[key] ?? 0) + Math.abs(value);
        }
        breakdownCount++;

        player.rating = newRating;
        player.totalPoints += Math.max(points, 0);
        player.stats.gamesPlayed++;

        const pIdx = players.indexOf(player);
        if (pIdx >= 0) {
          playerGames[pIdx]!++;
          if (ranks[teamIdx] === 1) playerWins[pIdx]!++;
        }

        player.stats.winStreak = ranks[teamIdx] === 1 ? player.stats.winStreak + 1 : 0;

        // Per-player convergence
        if (pIdx >= 0 && !playerConverged[pIdx]) {
          const est = newRating.mu - 2 * newRating.sigma;
          if (Math.abs(est - player.trueSkill) < 3) {
            playerConvergence[pIdx] = m;
            playerConverged[pIdx] = true;
          }
        }

        playerSnapshots.push({
          playerId: player.id,
          name: player.name,
          trueSkill: player.trueSkill,
          estimatedSkill: Math.round(newRating.mu - 2 * newRating.sigma),
          mu: newRating.mu,
          sigma: newRating.sigma,
          totalPoints: player.totalPoints,
          matchPoints: points,
          rank: ranks[teamIdx]!,
        });
      });
    });

    for (const p of players) {
      if (!matchPlayers.includes(p)) {
        playerSnapshots.push({
          playerId: p.id,
          name: p.name,
          trueSkill: p.trueSkill,
          estimatedSkill: Math.round(p.rating.mu - 2 * p.rating.sigma),
          mu: p.rating.mu,
          sigma: p.rating.sigma,
          totalPoints: p.totalPoints,
          matchPoints: 0,
          rank: 0,
        });
      }
    }

    matchSnapshots.push({ matchIndex: m, playerSnapshots });
    const avgSigma = players.reduce((s, p) => s + p.rating.sigma, 0) / players.length;
    sigmaOverTime.push(avgSigma);
  }

  // ── Compute analytics ──────────────────────────────────────

  const trueSkills = players.map((p) => p.trueSkill);
  const estimatedSkills = players.map((p) => p.rating.mu - 2 * p.rating.sigma);
  const totalPointsArr = players.map((p) => p.totalPoints);

  const skillCorrelation = pearsonCorrelation(trueSkills, estimatedSkills);
  const pointsCorrelation = pearsonCorrelation(trueSkills, totalPointsArr);

  const skillMAE =
    players.reduce((s, p) => s + Math.abs(p.trueSkill - (p.rating.mu - 2 * p.rating.sigma)), 0) /
    players.length;

  const trueRanks = rankArray(trueSkills);
  const pointsRanks = rankArray(totalPointsArr);
  const rankMAE =
    trueRanks.reduce((s, r, i) => s + Math.abs(r - pointsRanks[i]!), 0) / players.length;

  // Convergence
  let convergenceMatch = numMatches;
  for (let m = Math.min(10, matchSnapshots.length - 1); m < matchSnapshots.length; m++) {
    const snap = matchSnapshots[m]!;
    const estForSnap = snap.playerSnapshots.map((p) => p.estimatedSkill);
    const trueForSnap = snap.playerSnapshots.map((p) => p.trueSkill);
    const estRanks = rankArray(estForSnap);
    const trueRanksSnap = rankArray(trueForSnap);
    let correct = 0;
    let total = 0;
    for (let i = 0; i < estRanks.length; i++) {
      for (let j = i + 1; j < estRanks.length; j++) {
        total++;
        if (
          Math.sign(estRanks[i]! - estRanks[j]!) ===
          Math.sign(trueRanksSnap[i]! - trueRanksSnap[j]!)
        )
          correct++;
      }
    }
    if (total > 0 && correct / total >= 0.8) {
      convergenceMatch = m;
      break;
    }
  }

  // Point distribution
  const bucketSize = 10;
  const pointDistribution: SimulationAnalytics["pointDistribution"] = [];
  for (let b = -100; b < 100; b += bucketSize) {
    pointDistribution.push({
      bucketMin: b,
      bucketMax: b + bucketSize,
      count: allMatchPoints.filter((p) => p >= b && p < b + bucketSize).length,
    });
  }

  // Multiplier impact
  const multiplierImpact: Record<string, number> = {};
  for (const [key, sum] of Object.entries(breakdownSums)) {
    multiplierImpact[key] = breakdownCount > 0 ? sum / breakdownCount : 0;
  }

  // Gini
  const gini = giniCoefficient(totalPointsArr);

  // Head-to-head
  const headToHead = {
    playerIds: players.map((p) => p.id),
    names: players.map((p) => p.name),
    wins: h2hWins,
  };

  // Win rate by skill tier
  const sortedBySkill = [...players].sort((a, b) => a.trueSkill - b.trueSkill);
  const tierSize = Math.ceil(numPlayers / 3);
  const tiers = [
    { tier: "Low", players: sortedBySkill.slice(0, tierSize) },
    { tier: "Mid", players: sortedBySkill.slice(tierSize, tierSize * 2) },
    { tier: "High", players: sortedBySkill.slice(tierSize * 2) },
  ];
  const winRateByTier = tiers
    .filter((t) => t.players.length > 0)
    .map((t) => {
      const indices = t.players.map((p) => players.indexOf(p));
      const totalW = indices.reduce((s, i) => s + playerWins[i]!, 0);
      const totalG = indices.reduce((s, i) => s + playerGames[i]!, 0);
      return {
        tier: t.tier,
        avgTrueSkill: t.players.reduce((s, p) => s + p.trueSkill, 0) / t.players.length,
        winRate: totalG > 0 ? totalW / totalG : 0,
        avgPoints: t.players.reduce((s, p) => s + p.totalPoints, 0) / t.players.length,
      };
    });

  // Per-player convergence
  const perPlayerConvergence = players.map((p, i) => ({
    playerId: p.id,
    name: p.name,
    trueSkill: p.trueSkill,
    convergenceMatch: playerConvergence[i]!,
  }));

  // Confidence intervals (μ ± 2σ)
  const confidenceIntervals = players.map((p) => {
    const est = p.rating.mu - 2 * p.rating.sigma;
    const lower = p.rating.mu - 2 * p.rating.sigma;
    const upper = p.rating.mu + 2 * p.rating.sigma;
    return {
      playerId: p.id,
      name: p.name,
      trueSkill: p.trueSkill,
      lower,
      upper,
      estimatedSkill: est,
      withinCI: p.trueSkill >= lower && p.trueSkill <= upper,
    };
  });

  // ELO baseline
  const eloBaselineCorrelation = runEloBaseline(
    players.map((p) => ({
      ...p,
      rating: rating(),
      totalPoints: 0,
      stats: { gamesPlayed: 0, winStreak: 0 },
    })),
    numMatches,
    teamSize,
    numTeams,
  );

  // Points volatility
  const pointsVolatility = calcStddev(allMatchPoints);

  // Ranking stability
  const topK = Math.min(5, numPlayers);
  const rankingStability: number[] = [];
  for (let i = 1; i < matchSnapshots.length; i++) {
    const prevSnap = matchSnapshots[i - 1]!;
    const currSnap = matchSnapshots[i]!;
    const prevRanked = [...prevSnap.playerSnapshots]
      .sort((a, b) => b.estimatedSkill - a.estimatedSkill)
      .slice(0, topK)
      .map((p) => p.playerId);
    const currRanked = [...currSnap.playerSnapshots]
      .sort((a, b) => b.estimatedSkill - a.estimatedSkill)
      .slice(0, topK)
      .map((p) => p.playerId);
    const overlap = prevRanked.filter((id) => currRanked.includes(id)).length;
    rankingStability.push(overlap / topK);
  }

  return {
    config,
    players: players.map((p, i) => ({
      id: p.id,
      name: p.name,
      trueSkill: p.trueSkill,
      finalMu: p.rating.mu,
      finalSigma: p.rating.sigma,
      finalEstimatedSkill: p.rating.mu - 2 * p.rating.sigma,
      finalTotalPoints: p.totalPoints,
      gamesPlayed: p.stats.gamesPlayed,
      winRate: playerGames[i]! > 0 ? playerWins[i]! / playerGames[i]! : 0,
      convergenceMatch: playerConvergence[i]!,
    })),
    matchSnapshots,
    analytics: {
      skillCorrelation,
      pointsCorrelation,
      skillMAE,
      rankMAE,
      convergenceMatch,
      pointDistribution,
      multiplierImpact,
      predictionAccuracy: totalPredictions > 0 ? correctPredictions / totalPredictions : 0,
      sigmaOverTime,
      giniCoefficient: gini,
      headToHead,
      winRateByTier,
      perPlayerConvergence,
      confidenceIntervals,
      eloBaselineCorrelation,
      pointsVolatility,
      rankingStability,
    },
  };
};
